import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import api, { apiError, fmtTime } from "@/lib/api";
import { Button } from "@/components/ui/button";

const INPUT = "bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-3 py-2 text-sm text-slate-200 w-full font-mono";

export default function InviteAccept() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [bad, setBad] = useState("");
  const [f, setF] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/enterprise/invitations/preview/${token}`)
      .then((r) => setInv(r.data))
      .catch((e) => setBad(apiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) { setError("Passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      await api.post("/enterprise/invitations/accept", {
        token, email: inv.email, name: f.name, password: f.password,
      });
      setDone(true);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const invalid = bad || (inv && inv.status !== "VALID" &&
    `This invitation is ${inv.status.toLowerCase()}. Invitations are one-time, time-limited, and revocable.`);

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center p-4" data-testid="invite-accept-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/hussar-logo.png" alt="Hussar Treasury" className="w-64 mx-auto" />
          <div className="mt-3 text-[10px] font-mono tracking-widest text-slate-500 uppercase">Private Enterprise Invitation</div>
        </div>
        <div className="panel p-6">
          {done ? (
            <div className="text-center space-y-4" data-testid="ia-success">
              <ShieldCheck className="w-10 h-10 text-[#D4AF37] mx-auto" />
              <div className="text-sm text-slate-200 font-mono">ONBOARDING COMPLETE</div>
              <p className="text-xs text-slate-500">Your enterprise workspace is active. This invitation is now permanently consumed.</p>
              <Link to="/login" className="inline-block text-xs text-[#D4AF37] font-mono hover:underline" data-testid="ia-login-link">Sign in</Link>
            </div>
          ) : invalid ? (
            <div className="text-center space-y-3" data-testid="ia-invalid">
              <ShieldAlert className="w-10 h-10 text-red-400 mx-auto" />
              <div className="text-xs text-red-300 font-mono">{invalid}</div>
              <Link to="/login" className="inline-block text-xs text-slate-500 font-mono hover:text-slate-300">Back to sign in</Link>
            </div>
          ) : inv ? (
            <form onSubmit={submit} className="space-y-3" data-testid="ia-form">
              <div className="text-sm font-mono text-slate-200">{inv.org_name}</div>
              <div className="text-[11px] font-mono text-slate-500" data-testid="ia-status">
                Seat: {inv.role?.replace(/_/g, " ")} · bound to <span className="text-slate-300">{inv.email}</span> · expires {fmtTime(inv.expires_at)}
              </div>
              <input data-testid="ia-name-input" required className={INPUT} placeholder="Your full name"
                     value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <input data-testid="ia-password-input" required type="password" className={INPUT} placeholder="Password (12+ chars, upper/lower/digit/symbol)"
                     value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
              <input data-testid="ia-confirm-input" required type="password" className={INPUT} placeholder="Confirm password"
                     value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
              {error && <div className="text-xs text-red-400 font-mono" data-testid="ia-error">{error}</div>}
              <Button data-testid="ia-submit-btn" disabled={busy}
                      className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                {busy ? "ACTIVATING…" : "ACCEPT INVITATION & ONBOARD"}
              </Button>
            </form>
          ) : (
            <div className="text-center text-xs text-slate-500 font-mono py-6" data-testid="ia-loading">VALIDATING INVITATION…</div>
          )}
        </div>
      </div>
    </div>
  );
}
